import {noop} from '@benchling/js-utils';
import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useMemo} from 'react';

import {AntibodyFormat, DistinctChain, DistinctDomainWithChainIndex} from 'antibodies/AntibodyTypedefs';
import {
  chainColor,
  distinctChainToFieldName,
  domainColor,
  distinctDomainToFieldName,
  distinctDomainToKey,
  formatToSortedDistinctChains,
  formatToSortedDistinctDomainsWithChainIndex,
} from 'antibodies/AntibodyUtils';
import {AntibodyDomainSchemaFieldSpec} from 'antibodies/schema-configuration/AntibodySchemaInterfaceConstants';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {AntibodyDomainType} from 'graphql-client/__generated/graphqlGlobalTypes';
import FieldTableHeader from 'registry/field/FieldTableHeader';
import SelectSearchableDropdown from 'ui-library/dropdowns/SelectSearchableDropdown';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {OptionData} from 'ui-library/menus/MenuConstants';

import {TagSchemaSummaryGraphQL} from '../../../graphql-fragments/schemas/tag-schemas/__generated/TagSchemaSummaryGraphQL';

export const ANY_AA_SEQUENCE_DISPLAY = 'Any AA sequence';
const ANY_AA_SEQUENCE_VALUE = ModelTypeConstants.PROTEIN;

function ProteinSchemaOption({iconId, label}: {iconId: BenchlingIconName; label: string}): JSX.Element {
  return (
    <div>
      <BenchlingIcon className={css(styles.icon)} icon={iconId} />
      <span>{label}</span>
    </div>
  );
}

/**
 * Renders the domain type and specificity, with a swatch corresponding to the color for that domain in the
 * antibody viz.
 * Exported for testing.
 * @package
 */
export function DomainTypeAndSpecificityCell({
  distinctDomain,
}: {
  distinctDomain: DistinctDomainWithChainIndex;
}): JSX.Element {
  return (
    <div className={classNames('simpleDivTable-cell', css(styles.tableCell))}>
      <div className={css(styles.swatch)} style={{backgroundColor: domainColor({distinctDomain}).fill}} />
      <span>{distinctDomainToFieldName(distinctDomain)}</span>
    </div>
  );
}

/**
 * Renders the chain type and specificity, with a circle corresponding to the color for that chain in the
 * antibody viz.
 * Exported for testing.
 * @package
 */
export function ChainTypeAndSpecificityCell({
  distinctChain,
  chainIndex,
}: {
  distinctChain: DistinctChain;
  chainIndex: number;
}): JSX.Element {
  return (
    <div className={classNames('simpleDivTable-cell', css(styles.tableCell))}>
      <div className={css(styles.dot)} style={{backgroundColor: chainColor(chainIndex)}} />
      <span>{distinctChainToFieldName(distinctChain)}</span>
    </div>
  );
}

export interface AntibodyConfigurationTableProps {
  /* The antibody format for which an antibody schema is being configured */
  format: AntibodyFormat;
  /* All tag schemas that currently exist on the registry */
  allSchemas: Array<TagSchemaSummaryGraphQL>;
  disabled: boolean;
  onChangeDomainSchema: (
    domainType: string,
    specificity: string | null,
    schemaOrInterfaceId: string | null
  ) => void;
  domainSchemaFieldSpecs: Array<AntibodyDomainSchemaFieldSpec>;
}

export default function AntibodyConfigurationTable({
  format,
  allSchemas,
  disabled,
  onChangeDomainSchema,
  domainSchemaFieldSpecs,
}: AntibodyConfigurationTableProps): JSX.Element {
  const sortedDistinctChains = useMemo(() => formatToSortedDistinctChains(format), [format]);
  const sortedDistinctDomains = useMemo(() => formatToSortedDistinctDomainsWithChainIndex(format), [format]);
  const linkedSchemasByDomain = useMemo(
    () =>
      domainSchemaFieldSpecs.reduce(
        (acc, spec) => {
          acc[distinctDomainToKey({type: spec.domainType, specificity: spec.specificity})] =
            spec.schemaOrInterfaceId;
          return acc;
        },
        {} as Record<string, string | null>
      ),
    [domainSchemaFieldSpecs]
  );

  // @ts-expect-error TS does not believe that TagSchemaSupportedFolderItemType overlaps with
  // ModelTypeConstants despite the former being comprised entirely of members of the latter
  const proteinSchemas = allSchemas.filter((schema) => schema.folderItemType === ModelTypeConstants.PROTEIN);
  const proteinSchemasById = proteinSchemas.reduce<Record<string, TagSchemaSummaryGraphQL>>(
    (acc, proteinSchema) => {
      acc[proteinSchema.id] = proteinSchema;
      return acc;
    },
    {}
  );
  const optionDatas: Array<OptionData> = [
    {icon: 'protein', name: ANY_AA_SEQUENCE_DISPLAY, value: ANY_AA_SEQUENCE_VALUE},
    ...proteinSchemas.map(({iconId, id, name}) => ({
      icon: (iconId ?? 'protein') as BenchlingIconName,
      name,
      value: id,
    })),
  ];

  function onSelectDomainSchema(
    domainType: AntibodyDomainType,
    specificity: string | null,
    values: Array<string>
  ): void {
    const currentValue =
      linkedSchemasByDomain[distinctDomainToKey({type: domainType, specificity})] ?? ANY_AA_SEQUENCE_VALUE;
    const newValues = values.filter((v) => v !== currentValue);
    if (newValues.length === 0) {
      // Clicked on the already selected value, so make no changes
      return;
    }
    assert(newValues.length === 1, 'Attempted to select more than one value on a single value field');
    // Selecting any AA sequence is represented as null in domain specs but is ANY_AA_SEQUENCE_VALUE in the dropdown optiondatas
    const newSchema = newValues[0] === ANY_AA_SEQUENCE_VALUE ? null : newValues[0];
    onChangeDomainSchema(domainType, specificity, newSchema);
  }

  function renderProteinSchemaDropdown({
    disabled,
    currentValue,
    onSelect,
  }: {
    disabled: boolean;
    currentValue: string;
    onSelect: (values: Array<string>) => void;
  }): JSX.Element {
    return (
      <SelectSearchableDropdown
        disabled={disabled}
        menuProps={{isMulti: false}}
        noneSelectedMessage={<ProteinSchemaOption iconId="protein" label="Any AA Sequence" />}
        onSelect={onSelect}
        optionDatas={optionDatas}
        renderSelectedValues={([schemaId]) => {
          const proteinSchema = proteinSchemasById[schemaId] ?? {
            iconId: 'protein',
            name: ANY_AA_SEQUENCE_DISPLAY,
          };
          return (
            <ProteinSchemaOption
              iconId={(proteinSchema.iconId ?? 'protein') as BenchlingIconName}
              label={proteinSchema.name}
            />
          );
        }}
        selectedValues={[currentValue]}
        closeOnInsideClick={true}
      />
    );
  }

  return (
    <div className={classNames('simpleDivTable', css(styles.table))}>
      <FieldTableHeader
        isDisabled={true}
        onAddField={null}
        subheader={<>Definition</>}
        title={BenchlingConfig.MOLBIO_ENABLE_ANTIBODY_CHAINS ? 'Format fields' : 'Format domain field'}
      />
      {BenchlingConfig.MOLBIO_ENABLE_ANTIBODY_CHAINS &&
        sortedDistinctChains.map((distinctChain, chainIndex) => {
          const key = distinctChainToFieldName(distinctChain);
          return (
            <div key={key} className={classNames('simpleDivTable-row', css(styles.tableRow))}>
              <ChainTypeAndSpecificityCell distinctChain={distinctChain} chainIndex={chainIndex} />
              <div className="simpleDivTable-cell">
                {renderProteinSchemaDropdown({
                  // Always disable for now.
                  disabled: true,
                  currentValue: ANY_AA_SEQUENCE_VALUE,
                  onSelect: noop,
                })}
              </div>
            </div>
          );
        })}
      {sortedDistinctDomains.map((distinctDomain) => {
        const {type, specificity} = distinctDomain;
        const key = distinctDomainToFieldName(distinctDomain);
        return (
          <div key={key} className={classNames('simpleDivTable-row', css(styles.tableRow))}>
            <DomainTypeAndSpecificityCell distinctDomain={distinctDomain} />
            <div className="simpleDivTable-cell">
              {renderProteinSchemaDropdown({
                disabled: !BenchlingConfig.MOLBIO_ENABLE_ANTIBODY_DOMAIN_SCHEMAS || disabled,
                currentValue:
                  linkedSchemasByDomain[distinctDomainToKey(distinctDomain)] ?? ANY_AA_SEQUENCE_VALUE,
                onSelect: (values) => {
                  onSelectDomainSchema(type, specificity, values);
                },
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const styles = StyleSheet.create({
  table: {
    marginTop: '24px',
  },
  tableRow: {
    backgroundColor: Color.WHITE,
  },
  tableCell: {
    display: 'flex',
  },
  swatch: {
    width: '4px',
    minWidth: '4px',
    marginRight: '4px',
    flex: '0 0 4px',
  },
  dot: {
    width: '12px',
    minWidth: '12px',
    marginRight: '4px',
    flex: '0 0 12px',
    borderRadius: '50%',
    alignSelf: 'center',
    height: '12px',
    minHeight: '12px',
  },
  icon: {
    marginRight: '4px',
  },
});
